<template>
  <div class="timelapse-page">
    <div class="page-title"><div><h2>延时摄影</h2><p>查看项目监控通道的采集进度、影像和成片</p></div><el-button icon="el-icon-refresh" @click="loadPlans">刷新</el-button></div>
    <el-empty v-if="!loading && !plans.length" description="管理员尚未配置延时摄影计划" />
    <div v-loading="loading" class="plan-grid">
      <section v-for="plan in plans" :key="plan.id" class="plan-card">
        <div class="card-head">
          <div><h3>{{ plan.name }}</h3><div class="channel-name"><el-tag size="mini" :type="plan.source_type === 'ys7' ? 'warning' : 'info'">{{ plan.source_type === 'ys7' ? '萤石云' : '国标' }}</el-tag> {{ plan.channel_name || plan.channel_id }}</div></div>
          <el-tag :type="plan.status === 'running' ? 'success' : plan.status === 'paused' ? 'warning' : 'info'">{{ statusText(plan.status) }}</el-tag>
        </div>
        <div class="preview" @click="openPreview(plan)">
          <img v-if="previewUrls[plan.id]" :src="previewUrls[plan.id]" />
          <div v-else class="preview-empty"><i class="el-icon-video-camera"></i><span>{{ plan.frame_count ? '点击查看最新采集图片' : '等待首次采集' }}</span></div>
        </div>
        <div class="metrics"><div><b>{{ plan.frame_count || 0 }}</b><span>成功图片</span></div><div><b>{{ plan.failed_count || 0 }}</b><span>缺帧记录</span></div><div><b>{{ plan.last_capture_at ? plan.last_capture_at.slice(5, 16) : '—' }}</b><span>最近采集</span></div></div>
        <div class="plan-meta">{{ plan.start_date }}{{ plan.end_date ? ' 至 ' + plan.end_date : ' 起' }} · 每 {{ Math.round(plan.interval_sec / 60) }} 分钟</div>
        <div v-if="plan.latest_render && plan.latest_render.status !== 'success'" class="render-status">成片{{ plan.latest_render.status === 'failed' ? '失败：' + plan.latest_render.error_message : '处理中' }}</div>
        <div class="card-actions">
          <el-button size="mini" @click="showSlots(plan)">采集时间轴</el-button>
          <el-button v-if="plan.latest_render && plan.latest_render.status === 'success'" size="mini" type="primary" plain @click="playRender(plan)">预览成片</el-button>
        </div>
        <video v-if="activeVideoPlan === plan.id && videoUrl" :src="videoUrl" controls class="video-player"></video>
      </section>
    </div>

    <el-dialog class="timelapse-no-effect timelapse-dark-dialog" title="采集时间轴" :visible.sync="slotsVisible" :modal="false" width="760px" append-to-body :modal-append-to-body="true" :z-index="2147483000">
      <el-table v-loading="slotsLoading" :data="slots" size="small" max-height="470">
        <el-table-column prop="scheduled_at" label="计划时间" width="180" />
        <el-table-column prop="captured_at" label="实际采集时间" width="180"><template slot-scope="scope">{{ scope.row.captured_at || '—' }}</template></el-table-column>
        <el-table-column prop="status" label="状态" width="100"><template slot-scope="scope"><el-tag :type="slotStatusType(scope.row.status)">{{ slotStatusText(scope.row.status) }}</el-tag></template></el-table-column>
        <el-table-column prop="failure_reason" label="原因" min-width="160"><template slot-scope="scope">{{ scope.row.failure_reason || '—' }}</template></el-table-column>
        <el-table-column label="预览" width="90"><template slot-scope="scope"><el-button v-if="scope.row.status === 'success'" type="text" @click="previewFrame(scope.row)">查看图片</el-button></template></el-table-column>
      </el-table>
      <div class="slots-pagination"><el-pagination :current-page="slotsPage" :page-size="slotsSize" :total="slotsTotal" layout="total, prev, pager, next, jumper" @current-change="loadSlots" /></div>
    </el-dialog>
    <el-dialog class="timelapse-no-effect timelapse-dark-dialog" title="图片预览" :visible.sync="imageVisible" :modal="false" width="900px" append-to-body :modal-append-to-body="true" :z-index="2147483000"><img v-if="imageUrl" :src="imageUrl" class="image-large" /></el-dialog>
  </div>
</template>

<script>
import axios from 'axios'
import APP from '@/utils/base'
import request from '@/utils/request'
import { getToken } from '@/utils/auth'

export default {
  name: 'TimelapseProjectView',
  data () { return { loading: false, plans: [], slots: [], slotsVisible: false, slotsLoading: false, slotsPlanId: 0, slotsPage: 1, slotsSize: 20, slotsTotal: 0, imageVisible: false, previewUrls: {}, frameLoadRunId: 0, videoUrl: '', imageUrl: '', activeVideoPlan: null } },
  mounted () { this.loadPlans() },
  beforeDestroy () {
    Object.keys(this.previewUrls).forEach(id => URL.revokeObjectURL(this.previewUrls[id]))
    if (this.videoUrl) URL.revokeObjectURL(this.videoUrl)
    if (this.imageUrl) URL.revokeObjectURL(this.imageUrl)
  },
  methods: {
    async loadPlans () {
      this.loading = true
      try {
        const res = await request.get('/projectIndex/timelapse/plans')
        this.plans = res.data.list || []
        const runId = ++this.frameLoadRunId
        Object.keys(this.previewUrls).forEach(id => URL.revokeObjectURL(this.previewUrls[id]))
        this.previewUrls = {}
        this.plans.forEach(plan => { if (plan.frame_count) this.loadLatestFrame(plan, runId).catch(() => {}) })
      } finally { this.loading = false }
    },
    async loadLatestFrame (plan, runId = this.frameLoadRunId) {
      const res = await request.get('/projectIndex/timelapse/slots', { params: { plan_id: plan.id, page: 1, size: 1, status: 'success' } })
      const frame = (res.data.list || [])[0]
      if (!frame) return
      const url = await this.mediaUrl('frame', frame.id)
      if (runId !== this.frameLoadRunId) {
        URL.revokeObjectURL(url)
        return
      }
      this.previewUrls = Object.assign({}, this.previewUrls, { [plan.id]: url })
    },
    async mediaUrl (type, id) {
      const base = APP.resolveApiBaseUrl()
      const response = await axios.get(base + '/projectIndex/timelapse/media', { params: { type, id }, headers: { 'X-Token': getToken() }, responseType: 'blob' })
      return URL.createObjectURL(response.data)
    },
    async openPreview (plan) {
      if (!plan.frame_count) return this.$message.info('当前还没有成功采集的图片')
      if (!this.previewUrls[plan.id]) {
        try { await this.loadLatestFrame(plan) } catch (error) { return this.$message.error(error.message || '图片读取失败') }
      }
      this.setImageUrl(this.previewUrls[plan.id] || ''); this.imageVisible = !!this.imageUrl
    },
    async showSlots (plan) {
      this.slotsPlanId = plan.id; this.slotsPage = 1; this.slotsTotal = 0; this.slots = []; this.slotsVisible = true
      await this.loadSlots(1)
    },
    async loadSlots (page) {
      if (page) this.slotsPage = page
      if (!this.slotsPlanId) return
      this.slotsLoading = true
      try {
        const res = await request.get('/projectIndex/timelapse/slots', { params: { plan_id: this.slotsPlanId, page: this.slotsPage, size: this.slotsSize } })
        this.slots = res.data.list || []; this.slotsTotal = Number(res.data.total || 0)
      } catch (error) {
        this.$message.error(error.message || '采集时间轴读取失败')
      } finally { this.slotsLoading = false }
    },
    async playRender (plan) {
      if (this.videoUrl) URL.revokeObjectURL(this.videoUrl)
      this.videoUrl = ''
      try {
        this.videoUrl = await this.mediaUrl('video', plan.latest_render.id)
        this.activeVideoPlan = plan.id
      } catch (error) { this.$message.error(error.message || '成片读取失败') }
    },
    async previewFrame (row) {
      try {
        this.setImageUrl(await this.mediaUrl('frame', row.id))
        this.imageVisible = true
      } catch (error) { this.$message.error(error.message || '图片读取失败') }
    },
    setImageUrl (url) {
      if (this.imageUrl && !Object.values(this.previewUrls).includes(this.imageUrl)) URL.revokeObjectURL(this.imageUrl)
      this.imageUrl = url
    },
    slotStatusText (status) { return ({ success: '成功', failed: '缺帧', pending: '排队中', processing: '处理中', cancelled: '已取消' })[status] || status },
    slotStatusType (status) { return ({ success: 'success', failed: 'danger', pending: 'warning', processing: 'warning', cancelled: 'info' })[status] || 'info' },
    statusText (status) { return ({ running: '采集中', paused: '已暂停', stopped: '已停止', completed: '已完成' })[status] || status }
  }
}
</script>

<style scoped>
.timelapse-page { min-height: 100%; padding: 28px 34px; color: #d9f4ff; background: transparent; box-sizing: border-box; }
.page-title, .card-head, .card-actions { display: flex; align-items: center; justify-content: space-between; }
.page-title { margin-bottom: 22px; } h2 { margin: 0 0 6px; font-size: 22px; color: #b8f8ff; text-shadow: 0 0 12px rgba(0, 207, 255, .28); } .page-title p { margin: 0; color: #91b5ca; }
.page-title ::v-deep .el-button { color: #a8efff; border-color: rgba(28, 190, 236, .55); background: rgba(3, 42, 79, .7); }
.page-title ::v-deep .el-button:hover { color: #fff; border-color: #00d9ff; background: rgba(0, 119, 175, .65); }
.plan-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(340px, 1fr)); gap: 18px; }
.plan-card { padding: 18px; background: linear-gradient(145deg, rgba(10, 48, 86, .94), rgba(4, 25, 53, .94)); border: 1px solid rgba(29, 185, 229, .34); border-radius: 8px; box-shadow: 0 7px 24px rgba(0, 5, 20, .38), inset 0 0 18px rgba(0, 175, 224, .06); }
.card-head h3 { margin: 0 0 7px; font-size: 17px; color: #e0faff; } .channel-name, .plan-meta { color: #91afc4; font-size: 13px; }
.preview { height: 190px; margin-top: 16px; overflow: hidden; border: 1px solid rgba(32, 176, 225, .25); border-radius: 4px; background: rgba(2, 15, 34, .8); cursor: pointer; }
.preview img { width: 100%; height: 100%; object-fit: contain; } .preview-empty { height: 100%; display: flex; flex-direction: column; gap: 10px; justify-content: center; align-items: center; color: #7898b2; } .preview-empty i { font-size: 36px; color: #44bedf; }
.metrics { display: flex; justify-content: space-around; padding: 17px 0; } .metrics div { display: flex; flex-direction: column; align-items: center; gap: 5px; } .metrics b { font-size: 20px; color: #d7faff; text-shadow: 0 0 10px rgba(0, 193, 255, .2); } .metrics span { color: #8ca9bd; font-size: 12px; }
.plan-meta { padding-bottom: 14px; border-bottom: 1px solid rgba(37, 160, 208, .26); } .card-actions { justify-content: flex-start; flex-wrap: wrap; gap: 6px; padding-top: 14px; }
.card-actions ::v-deep .el-button { color: #aeeeff; border-color: rgba(37, 186, 225, .42); background: rgba(3, 44, 81, .78); }
.card-actions ::v-deep .el-button:hover { color: #fff; border-color: #00d9ff; background: rgba(0, 121, 175, .72); }
.card-actions ::v-deep .el-button--primary { color: #e8fcff; border-color: rgba(0, 209, 255, .72); background: linear-gradient(135deg, #087fae, #0564b2); }
.card-actions ::v-deep .el-button--primary.is-plain { color: #83e8ff; border-color: rgba(0, 209, 255, .58); background: rgba(3, 53, 91, .64); }
.render-status { padding-top: 10px; color: #ffc76a; font-size: 13px; line-height: 1.5; }
.slots-pagination { display: flex; justify-content: flex-end; padding-top: 16px; }
.video-player { display: block; width: 100%; max-height: 360px; margin-top: 16px; background: #000; } .image-large { display: block; max-width: 100%; max-height: 70vh; margin: 0 auto; }
.timelapse-page ::v-deep .el-empty__description p { color: #91afc4; }
.timelapse-page ::v-deep .el-empty__image svg { fill: #4ebbdc; }
</style>

<style>
.timelapse-dark-dialog .el-dialog { color: #d9f4ff; border: 1px solid rgba(26, 190, 231, .42); background: linear-gradient(145deg, rgba(8, 38, 73, .98), rgba(3, 18, 42, .99)); box-shadow: 0 14px 48px rgba(0, 5, 22, .58), 0 0 24px rgba(0, 168, 218, .12); }
.timelapse-dark-dialog .el-dialog__header { border-bottom: 1px solid rgba(37, 160, 208, .25); }
.timelapse-dark-dialog .el-dialog__title { color: #c9f8ff; }
.timelapse-dark-dialog .el-dialog__headerbtn .el-dialog__close { color: #8fc7da; }
.timelapse-dark-dialog .el-dialog__body { color: #c0dce9; }
/* 注意：Element UI 2.13 的表格单元格没有 .el-table__cell 类（2.15+ 才有），
   所以这里必须直接用 th / td 选择器，否则覆盖不到，表头与悬停行会露出默认白底。 */
.timelapse-dark-dialog .el-table, .timelapse-dark-dialog .el-table tr, .timelapse-dark-dialog .el-table th, .timelapse-dark-dialog .el-table td { color: #c9e8f3; border-color: rgba(55, 143, 182, .24); background: rgba(4, 24, 51, .56); }
.timelapse-dark-dialog .el-table th, .timelapse-dark-dialog .el-table thead { color: #8de9fa; background: rgba(7, 48, 82, .9); }
.timelapse-dark-dialog .el-table__header-wrapper, .timelapse-dark-dialog .el-table__body-wrapper { background: transparent; }
.timelapse-dark-dialog .el-table::before, .timelapse-dark-dialog .el-table--border::after { background: rgba(55, 143, 182, .24); }
.timelapse-dark-dialog .el-table--enable-row-hover .el-table__body tr:hover > td { background: rgba(10, 92, 133, .5); }
.timelapse-dark-dialog .el-pagination { color: #b9deec; }
.timelapse-dark-dialog .el-pagination button, .timelapse-dark-dialog .el-pager li { color: #b9deec; background: transparent; }
.timelapse-dark-dialog .el-pager li.active { color: #29d5ff; }
</style>



// WEBPACK FOOTER //
// src/views/Timelapse/index.vue